/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { EditorType } from '@ckeditor/ckeditor5-metadata';

import type { EditorPlugins } from '../../types/EditorPlugins';
import { findConflictingPlugins } from '../findConflictingPlugins/findConflictingPlugins';
import { findDisabledPlugins } from '../findDisabledPlugins/findDisabledPlugins';
import { findInvalidPlugins } from '../findInvalidPlugins/findInvalidPlugins';
import { findRequiredPlugins } from '../findRequiredPlugins/findRequiredPlugins';
import { getPluginByClassName } from '../getPluginByClassName/getPluginByClassName';

export function getPluginsToSelect( editorPlugins: EditorPlugins, pluginsToSelect: EditorPlugins, editorType: EditorType ): EditorPlugins {
	const newEditorPlugins = new Set( editorPlugins );

	// When selecting a group that includes conflicting features, verify if each item can be selected.
	pluginsToSelect = pluginsToSelect.reduce( ( nonConflictingPlugins, item ) => {
		const conflictingPlugins = findConflictingPlugins( [ item ], nonConflictingPlugins );

		if ( !conflictingPlugins.length ) {
			nonConflictingPlugins.push( item );
		}

		return nonConflictingPlugins;
	}, [] as EditorPlugins );

	const addPlugin = newEditorPlugins.add.bind( newEditorPlugins );
	const deletePlugin = newEditorPlugins.delete.bind( newEditorPlugins );

	findConflictingPlugins( [ ...newEditorPlugins ], pluginsToSelect )
		.flatMap( mapPluginToIncludeItsForceDeleteRequires )
		.forEach( deletePlugin );

	// The entire block is needed because a newly added plugin may conflict with another one.
	// While the previous loop cares to remove the conflicting plugins, the removed plugin could be a dependency
	// for another plugin that becomes invalid. Hence, we aim to remove it.
	findInvalidPlugins( [ ...newEditorPlugins ] )
		.filter( plugin => {
			// Potential problematic code.
			// Keep selected the _potential invalid_ plugin if plugins we want to select
			// match requirements of the invalid plugin.
			//
			// Example case: `FullPage` requires `SourceEditing`, but it can also work with `EnhancedSourceEditing`.
			// When toggling the source editing feature, `FullPage` should not be updated.
			return findRequiredPlugins( plugin, pluginsToSelect ).length;
		} )
		.flatMap( mapPluginToIncludeItsForceDeleteRequires )
		.forEach( deletePlugin );

	pluginsToSelect.forEach( plugin => {
		// When calculating the required plugins, consider the newly selected plugins too.
		//
		// Example case: both uploads require `PictureEditing`:
		// 1. `CKBox`
		// 2. `Uploadcare`
		//
		// `PictureEditing` has a one of dependency: `[ CKBox, Uploadcare ]`.
		// If `CKBox` exists, do not select `Uploadcare`, and vice-versa.
		//
		// When selecting `Uploadcare`, it will load `PictureEditing` too.
		// `PictureEditing` must not load `CKBox` as one of its dependency will be available in run-time.
		const requiredPlugins = findRequiredPlugins( plugin, [ ...newEditorPlugins ] );

		addPlugin( plugin );
		requiredPlugins.forEach( addPlugin );
	} );

	findDisabledPlugins( [ ...newEditorPlugins ], editorType )
		.flatMap( mapPluginToIncludeItsForceDeleteRequires )
		.forEach( deletePlugin );

	return [ ...newEditorPlugins ];
}

function mapPluginToIncludeItsForceDeleteRequires( pluginItem: EditorPlugins[ number ] ) {
	const { forceDeleteRequires } = getPluginByClassName( pluginItem );

	if ( forceDeleteRequires ) {
		return [ pluginItem, ...forceDeleteRequires ];
	}

	return [ pluginItem ];
}
